/**
 * Pure stroke clustering helpers for student-ink prompt context and verifier crops.
 * `studentInkClusters` filters out agent-authored and non-draw shapes before
 * grouping handwriting by spatial proximity.
 */

export interface InkBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface InkStroke<Id = string> {
  shapeId: Id;
  bounds: InkBounds;
  /** Epoch ms of the last create/change event for this stroke. */
  lastEditedAt: number;
}

/** Merge strokes into one cluster when their bounds are within this px gap. */
export const CLUSTER_GAP_PX = 40;

/**
 * Cushion grown around each stroke's bounding box when grouping strokes into
 * units of HANDWRITING (`studentInkClusters`). Two strokes share a cluster when
 * their cushioned boxes overlap, i.e. when their raw boxes are within 2× this
 * of each other — so callers pass `CLUSTER_CUSHION_PX.x * 2` and `.y * 2`.
 *
 * ANISOTROPIC, because the paper is. Sized against the ruled sheet
 * (RULE_SPACING = 74, a line of handwriting ~40-50px tall):
 *
 *  - `y` = 10. Successive LINES of writing leave a ~25-35px gap, so a 20px
 *    vertical link chains the parts of one construct — numerator / bar /
 *    denominator, a radical and its contents, a base and its exponent — without
 *    chaining two separate lines of work into one unit.
 *
 *  - `x` = 22. The parts of one NUMBER are separated horizontally by a digit
 *    space, which at this writing scale measured ~20px on a real answer — right
 *    at the old isotropic threshold, so `30` split into a `3` cluster and a `0`
 *    cluster, was labelled twice, and reached the model as two unrelated pieces
 *    of writing with no representation anywhere in the prompt in which the
 *    number `30` existed. 44px of horizontal reach reunites the digits. It is
 *    safe to be this generous sideways: what sits to the left and right of an
 *    answer is the agent's own printed content, which never enters this
 *    clustering (see `isStudentInk`), not other student work.
 *
 * Two pieces of writing side by side on ONE line — two answers in adjacent
 * boxes, say — will now merge if their gap is under 44px. That is the trade,
 * and it is the right way round: a merged cluster is one id naming writing the
 * model can still read in full off the screenshot, whereas a split number is a
 * digit the model cannot reassemble at all.
 *
 * Both numbers describe a gap between the strokes' RAW geometry — tldraw's
 * bounds, the centre of the pen. Cluster on boxes that have already been grown
 * to the ink they render (`inkedBounds`) and every gap measures short by two
 * stroke widths, which turns the 20px vertical link into 27px at the default
 * pen with nothing here saying so. `computeStudentInkClusters` therefore groups
 * first and inflates the union after.
 */
export const CLUSTER_CUSHION_PX = { x: 22, y: 10 };

/**
 * Default time window for callers that supply edit timestamps. Student-ink
 * prompt clustering uses a shared timestamp to group by spatial proximity only.
 */
export const CLUSTER_TEMPORAL_MS = 180_000;

/**
 * A linking distance, per axis.
 *
 * One number for both axes was the bug behind split two-digit answers. The two
 * directions are not alike on ruled paper: what separates the `3` from the `0`
 * of a `30` is horizontal whitespace inside one number, while what separates two
 * unrelated pieces of work is almost always vertical — the next rule line down.
 * A single gap has to be small enough not to chain the lines, which forces it
 * below the width of a digit space, which splits the number.
 */
export interface InkGap {
  x: number;
  y: number;
}

/** Read a gap as a pair, accepting the isotropic shorthand. */
function asGap(gap: number | InkGap): InkGap {
  return typeof gap === "number" ? { x: gap, y: gap } : gap;
}

export function boxesWithinGap(
  a: InkBounds,
  b: InkBounds,
  gap: number | InkGap,
): boolean {
  const { x: gapX, y: gapY } = asGap(gap);
  const dx = Math.max(0, Math.max(a.x - (b.x + b.w), b.x - (a.x + a.w)));
  const dy = Math.max(0, Math.max(a.y - (b.y + b.h), b.y - (a.y + a.h)));
  return dx <= gapX && dy <= gapY;
}

export function unionBounds(boxes: InkBounds[]): InkBounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const b of boxes) {
    if (b.x < minX) minX = b.x;
    if (b.y < minY) minY = b.y;
    if (b.x + b.w > maxX) maxX = b.x + b.w;
    if (b.y + b.h > maxY) maxY = b.y + b.h;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * Single-linkage clustering: two strokes link when they are BOTH within
 * `gapPx` spatially and within `temporalMs` of each other in edit time; a
 * cluster grows until no unlinked stroke links to any of its members.
 */
export function clusterStrokes<Id>(
  strokes: InkStroke<Id>[],
  opts: { gapPx?: number | InkGap; temporalMs?: number } = {},
): InkStroke<Id>[][] {
  const gap = opts.gapPx ?? CLUSTER_GAP_PX;
  const temporalMs = opts.temporalMs ?? CLUSTER_TEMPORAL_MS;

  const clusters: InkStroke<Id>[][] = [];
  const used = new Set<number>();
  for (let i = 0; i < strokes.length; i++) {
    if (used.has(i)) continue;
    const cluster: InkStroke<Id>[] = [strokes[i]];
    used.add(i);
    let grew = true;
    while (grew) {
      grew = false;
      for (let j = 0; j < strokes.length; j++) {
        if (used.has(j)) continue;
        const candidate = strokes[j];
        if (
          cluster.some(
            (c) =>
              boxesWithinGap(c.bounds, candidate.bounds, gap) &&
              Math.abs(c.lastEditedAt - candidate.lastEditedAt) <= temporalMs,
          )
        ) {
          cluster.push(candidate);
          used.add(j);
          grew = true;
        }
      }
    }
    clusters.push(cluster);
  }
  return clusters;
}
